/*
  Levendi — header behaviour. audit-build 2026-09-06, tuned 7 Sep 2026.

  Alex, 6 Sep: "not having a disappearing / simplified header?"
  Alex, 7 Sep: "when scrolling up the header should show the nav links as well,
                and make the animation slower/smoother."

  Three states, set as data-lv-header on <header-component>, styled in
  assets/levendi-system.css PART 8:

    top      first 120px of the page — the whole header: announcement bar
             (which lives above this element and simply scrolls away),
             logo/utility row, navigation row.

    pinned   past 120px, at rest or travelling up — logo/utility row AND the
             navigation row, both fully visible. This is the 7 Sep change:
             the returning header carries House of Levendi … Journal with it.
             The only thing still gone at this point is the announcement bar,
             and that is gone because it scrolled away on its own.
             (This state was called `condensed` on 6 Sep, when it faded the
             nav row out. It no longer condenses anything, so the name went.)

    hidden   past 120px, travelling down — the whole bar slides up out of view.

  Three hard rules the implementation obeys:

  1. NOTHING MAY CHANGE THE HEADER'S BOX HEIGHT. `--header-height` and
     `--header-group-height` are written from a ResizeObserver on that box and
     consumed by base.css (`margin-top: calc(var(--header-group-height) * -1)`
     on the first section) and by snippets/section.liquid. Changing the box
     mid-scroll would pull every page's content up on every non-transparent
     template. So every state is transform and opacity only — the box is
     116px at 1440 in all three states, exactly as it is at rest, and the
     header overlays rather than pushes.

  2. It never hides a header the visitor is using — an open drawer or menu, an
     open dialog, a locked body, or focus inside the header all hold `pinned`
     instead. This matters more than it looks: the drawer is a DESCENDANT of
     header-component, so a translated header would take an open drawer off
     screen with it. Under prefers-reduced-motion it never hides at all and
     the transitions are off.

  3. No layout reads in the scroll handler. The handler touches scrollTop
     (already dirty at scroll time), attribute writes, and class/selector
     queries — never offsetHeight/getBoundingClientRect — so nothing forces a
     synchronous layout at 60fps. Listeners are passive; work is coalesced
     into one requestAnimationFrame per frame.

  Motion (Ithaca grammar — slow, subtle, purposeful; 7 Sep tuning):
    hide          420ms   cubic-bezier(.22, 1, .36, 1)
    reveal        520ms   cubic-bezier(.22, 1, .36, 1)
    nav row       480ms after a 120ms delay, so it lands 80ms behind the bar —
                  staggered, never simultaneous.
  Hysteresis: 60px of continuous downward travel before it hides, 40px of
  continuous upward travel before it comes back. A scroll wobble smaller than
  that does nothing at all, so the header cannot flicker.
*/
(() => {
  const THRESHOLD = 120; // px of scroll before the header may leave at all
  const HIDE_AFTER = 60; // px of continuous downward travel before hiding
  const REVEAL_AFTER = 40; // px of continuous upward travel before revealing
  const NOISE = 2; // px — below this a movement is not a direction

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  /*
    DESKTOP NEVER HIDES — 18 Sep 2026 (Alex). On a laptop or desktop the
    transparent->solid transition stays exactly as it was, but once the header
    is solid it STAYS on screen: no hide-on-scroll-down. The hide/reveal
    grammar is mobile-and-tablet only (<1024px), which is the behaviour he
    likes on the phone and which this file was originally written for.

    Gated on a live matchMedia, not a one-shot innerWidth read, so rotating a
    tablet or dragging a window across the breakpoint is handled: the change
    listener below pins the header the moment the viewport becomes desktop, so
    a header hidden at 900px cannot stay stranded off-screen at 1200px.
    assets/levendi-system.css PART 8c is the CSS belt to this brace.
  */
  const desktop = window.matchMedia('(min-width: 1024px)');

  let el = null;
  let lastY = 0; // scroll position at the previous believed movement
  let anchor = 0; // scroll position where the current direction began
  let dir = 0; // -1 up, 1 down, 0 unknown / at top
  let state = '';
  let ticking = false;

  const scrollTop = () =>
    document.scrollingElement ? document.scrollingElement.scrollTop : window.scrollY || 0;

  /*
    The visitor is mid-interaction with the header — hold it on screen.

    Note the selector is deliberately narrow. A bare [aria-expanded="true"]
    test is wrong here: the drawer's own back-button carries
    aria-expanded="true" permanently, drawer open or shut, so that test holds
    the header on screen forever and the hide state never arrives. Only the
    things that actually mean "a panel is open" are listed.
  */
  const OPEN_SELECTOR = [
    '.menu-list__link[aria-expanded="true"]',
    '.dropdown-localization__button[aria-expanded="true"]',
    '.drawer-localization__button[aria-expanded="true"]',
    'details[open]',
  ].join(',');

  function inUse() {
    if (!el) return true;
    if (document.body.classList.contains('overflow-hidden')) return true;
    if (document.querySelector('dialog[open]')) return true;
    if (el.querySelector(OPEN_SELECTOR)) return true;
    const active = document.activeElement;
    if (active && active !== document.body && el.contains(active)) return true;
    return false;
  }

  function set(next) {
    if (next === state || !el) return;
    state = next;
    el.setAttribute('data-lv-header', next);
    /*
      Mirrored onto <html>, 7 Sep. Page-level furniture that glues itself to
      the underside of the header — the collection utility bar, and anything
      else that offsets by --lv-header-offset — needs to know the header has
      gone, and it needs to know it without a :has() selector re-evaluating on
      every scroll frame. One attribute on the root element does it.
    */
    document.documentElement.setAttribute('data-lv-header', next);
  }

  function update() {
    ticking = false;
    if (!el || !el.isConnected) return;

    const y = scrollTop();

    /* The first screen is always the full header, and it resets the travel
       accumulator so leaving the top needs a fresh 60px. */
    if (y <= THRESHOLD) {
      lastY = y;
      anchor = y;
      dir = 0;
      set('top');
      return;
    }

    const d = y - lastY;
    if (Math.abs(d) < NOISE) return;

    const next = d > 0 ? 1 : -1;
    if (next !== dir) {
      /* Direction flipped: the travel that decides the next state starts
         here, at the turning point. This is the hysteresis — a wobble of a
         few px back and forth never accumulates 40 or 60. */
      dir = next;
      anchor = lastY;
    }
    lastY = y;

    const travel = Math.abs(y - anchor);

    if (dir > 0) {
      if (travel < HIDE_AFTER) return;
      if (desktop.matches || reduce.matches || inUse()) {
        /* Blocked. Hold it on screen and make the next attempt earn its own
           60px, rather than firing the instant the drawer closes. */
        anchor = y;
        set('pinned');
        return;
      }
      set('hidden');
    } else {
      if (travel < REVEAL_AFTER) return;
      set('pinned');
    }
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  }

  function init() {
    const next = document.querySelector('header-component');
    if (!next) return;
    el = next;
    state = '';
    lastY = scrollTop();
    anchor = lastY;
    dir = 0;
    set(lastY > THRESHOLD ? 'pinned' : 'top');
  }

  /*
    A panel opening while the header is away must bring it straight back. The
    drawer and the localization list are DESCENDANTS of header-component, so a
    translated header takes them off screen with it — an open drawer must never
    coincide with the hidden state. Scoped to events inside the header only, so
    an ordinary click on the page does not summon it.
  */
  function onOpen(event) {
    if (state !== 'hidden' || !el) return;
    const target = event.target;
    if (target instanceof Node && el.contains(target)) {
      anchor = scrollTop();
      set('pinned');
    }
  }

  function start() {
    init();
    document.addEventListener('scroll', onScroll, { passive: true });
    /* Crossing into desktop width must bring a hidden header straight back. */
    const onBreakpoint = () => {
      if (desktop.matches && state === 'hidden') {
        anchor = scrollTop();
        set('pinned');
      }
    };
    if (desktop.addEventListener) desktop.addEventListener('change', onBreakpoint);
    else desktop.addListener(onBreakpoint);
    document.addEventListener('toggle', onOpen, true);
    document.addEventListener('click', onOpen, true);
    document.addEventListener('focusin', onOpen, true);
    /* The theme editor swaps the section node out from under us. */
    document.addEventListener('shopify:section:load', init);
    document.addEventListener('shopify:section:unload', init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
